import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, PackageCheck, TriangleAlert } from "lucide-react";
import {
  MAX_RETURN_NOTE_LENGTH,
  SHIPMENT_STAGE_LABELS,
  validateReceiveInput,
  type ReceiveReturnInput,
  type ReturnParcel,
  type ReturnPhotoInput,
  type ReturnReceipt,
  type WriteRejection,
} from "@estelle/domain";
import { useId, useRef, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { money } from "@/modules/orders/format";
import { EMPTY_DRAFT, type LineDraft } from "@/modules/orders/returns/drafts";
import { ImageLightbox } from "@/modules/orders/returns/ImageLightbox";
import { LineChecker } from "@/modules/orders/returns/LineChecker";
import { ReceiptView, StatusPill } from "@/modules/orders/returns/ReceiptView";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";
import { StageBadge } from "@/modules/orders/screens/TrackingParts";

/**
 * Receive a returned parcel (#529): open it, check each line, confirm.
 *
 * What is on the screen is read from the server and sent to the server. The form
 * holds only what the person has counted; "restocked", "pending" and "received"
 * are whatever the server answers after Confirm, drawn by `ReceiptView`.
 *
 * Confirm is safe to press twice: it is disabled the moment it is pressed, and
 * the server refuses a second receipt for the same shipment whatever the browser
 * does. A refusal such as "already received" reloads the parcel, which then
 * shows the receipt that exists.
 */

/** Photos as bytes, the one shape the repository takes. */
async function photoInputs(files: File[]): Promise<ReturnPhotoInput[]> {
  return Promise.all(
    files.map(async (file) => ({
      filename: file.name,
      contentType: file.type || "image/jpeg",
      data: new Uint8Array(await file.arrayBuffer()),
    })),
  );
}

type LineErrors = {
  line?: string;
  damagedReason?: string;
  missingReason?: string;
  photos?: string;
};

/** `lines.<id>` and its `.damagedReason`-style children, grouped by line. */
function errorsByLine(
  fields: Record<string, string> | undefined,
): Map<string, LineErrors> {
  const out = new Map<string, LineErrors>();
  for (const [key, message] of Object.entries(fields ?? {})) {
    const match =
      /^lines\.([^.]+)(?:\.(damagedReason|missingReason|photos))?$/.exec(key);
    if (!match?.[1]) continue;
    const entry = out.get(match[1]) ?? {};
    const child = match[2] as keyof LineErrors | undefined;
    entry[child ?? "line"] = message;
    out.set(match[1], entry);
  }
  return out;
}

function Facts({ parcel }: { parcel: ReturnParcel }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-semibold">{parcel.orderNumber}</h3>
        {parcel.stage ? (
          <StageBadge stage={parcel.stage} />
        ) : (
          <StatusPill tone="wait">Not delivered (Shopify)</StatusPill>
        )}
        {parcel.payment === "prepaid" ? (
          <Badge
            variant="outline"
            className="border-transparent bg-tint-blue text-tint-blue-foreground"
          >
            Prepaid
          </Badge>
        ) : parcel.payment === "cod" ? (
          <Badge variant="outline">Cash on delivery</Badge>
        ) : null}
      </div>
      {parcel.manual ? (
        <p className="text-sm">
          <span className="text-muted-foreground border-border mr-1.5 inline-flex rounded-sm border px-1 text-[0.6875rem] leading-4 font-medium">
            added manually
          </span>
          Added by {parcel.manual.addedByName ?? "someone"}:{" "}
          {parcel.manual.reason}
        </p>
      ) : null}
      <p className="text-muted-foreground text-sm">
        {parcel.shipmentName}
        {parcel.courier ? ` · ${parcel.courier}` : ""}
        {parcel.trackingNumber ? ` · ${parcel.trackingNumber}` : ""}
      </p>
      <p className="text-muted-foreground text-sm">
        {parcel.rtoDelivered
          ? "The courier says this parcel is back (RTO delivered)."
          : parcel.stage
            ? `Courier status: ${SHIPMENT_STAGE_LABELS[parcel.stage]}.`
            : "No tracking stage."}
        {parcel.returningDays !== null
          ? ` Returning for ${parcel.returningApproximate ? "about " : ""}${String(parcel.returningDays)} day${parcel.returningDays === 1 ? "" : "s"}${parcel.returningApproximate ? " (an estimate: no scan dates the return)" : ""}.`
          : ""}
        {parcel.shippedFrom ? ` Shipped from ${parcel.shippedFrom.name}.` : ""}
      </p>
    </div>
  );
}

function Warning({ children }: { children: string }) {
  return (
    <p
      role="status"
      className="flex items-start gap-2 rounded-md border border-transparent bg-tint-yellow p-3 text-sm text-tint-yellow-foreground"
    >
      <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

/** The refusal "this parcel was already received": someone else's confirm won. */
function isAlreadyReceived(rejection: WriteRejection): boolean {
  return (
    rejection.kind === "blocked" &&
    typeof rejection.details === "object" &&
    rejection.details !== null &&
    (rejection.details as { alreadyReceived?: unknown }).alreadyReceived ===
      true
  );
}

/**
 * Where a confirm is, in words, read from the receipt the server has recorded
 * (#558). A confirm with many lines takes as long as Shopify takes for each, one
 * after the other, so a spinner alone reads as a hang. The writes are not changed
 * and not made concurrent: this only reads the receipt as they happen.
 */
function progressWords(receipt: ReturnReceipt | null | undefined): {
  words: string;
  fraction: number;
} {
  if (!receipt) return { words: "Recording the receipt…", fraction: 0.05 };
  const writing = receipt.lines.filter(
    (line) => line.restockStatus !== "not_needed",
  );
  const done = writing.filter((line) => line.restockStatus === "accepted");
  const settled = writing.filter((line) => !line.restockInProgress).length;
  if (settled < writing.length) {
    return {
      words: `Putting item ${String(settled + 1)} of ${String(writing.length)} back in stock…`,
      fraction: 0.1 + 0.8 * (settled / writing.length),
    };
  }
  if (receipt.tagStatus !== "accepted" && receipt.tagInProgress) {
    return {
      words: "Adding the RTO Received tag in Shopify…",
      fraction: 0.95,
    };
  }
  return {
    words: `Finishing: ${String(done.length)} of ${String(writing.length)} items put back in stock…`,
    fraction: 0.98,
  };
}

function ParcelForm({
  parcel,
  onReceived,
  onAlreadyReceived,
}: {
  parcel: ReturnParcel;
  onReceived: (receipt: ReturnReceipt) => void;
  onAlreadyReceived: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const noteId = useId();
  const warehouseId = useId();
  const sending = useRef(false);

  const [warehouse, setWarehouse] = useState(parcel.defaultWarehouseId ?? "");
  const [note, setNote] = useState("");
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  /** Every line's product picture, in order, for the full-size viewer. */
  const gallery = parcel.lines.flatMap((line) =>
    line.imageUrl
      ? [
          {
            id: line.orderLineItemId,
            src: line.imageUrl,
            title: line.title,
            detail: [line.variantTitle, line.sku ? `SKU ${line.sku}` : null]
              .filter(Boolean)
              .join(" · "),
          },
        ]
      : [],
  );

  const draftOf = (id: string): LineDraft => drafts[id] ?? EMPTY_DRAFT;

  const shipped = new Map(
    parcel.lines.map((line) => [line.orderLineItemId, line.quantity] as const),
  );
  /** What the server will be sent, with the photos' sizes only (the checks count them). */
  const input = (): ReceiveReturnInput => ({
    warehouseId: warehouse,
    note: note.trim() === "" ? null : note.trim(),
    lines: parcel.lines.map((line) => {
      const draft = draftOf(line.orderLineItemId);
      return {
        orderLineItemId: line.orderLineItemId,
        good: draft.good,
        damaged: draft.damaged,
        missing: draft.missing,
        damagedReason: draft.damagedReason,
        missingReason: draft.missingReason,
        photos: draft.photos.map((file) => ({
          filename: file.name,
          contentType: file.type,
          data: new Uint8Array(0),
        })),
      };
    }),
  });

  // The same checks the server runs, so the button and the server agree.
  const problem = validateReceiveInput(input(), shipped);
  const fromServer =
    rejection?.kind === "invalid" ? rejection.fields : undefined;
  const unplaced = Object.entries(fromServer ?? {}).filter(
    ([key]) => !/^(lines(\.|$)|warehouseId$|note$)/.test(key),
  );
  const lineErrors = errorsByLine({
    ...(problem?.fields ?? {}),
    ...(fromServer ?? {}),
  });

  const totals = parcel.lines.reduce(
    (sum, line) => {
      const draft = draftOf(line.orderLineItemId);
      return {
        good: sum.good + draft.good,
        damaged: sum.damaged + draft.damaged,
        missing: sum.missing + draft.missing,
        left:
          sum.left +
          (line.quantity - draft.good - draft.damaged - draft.missing),
      };
    },
    { good: 0, damaged: 0, missing: 0, left: 0 },
  );

  const warehouseName =
    parcel.warehouses.find((choice) => choice.id === warehouse)?.name ?? null;
  const noWarehouse = warehouse === "";

  const confirm = useMutation({
    mutationFn: async () => {
      const wanted = input();
      return data.returns.receive(parcel.shipmentId, {
        ...wanted,
        lines: await Promise.all(
          parcel.lines.map(async (line, index) => {
            const draft = draftOf(line.orderLineItemId);
            const base = wanted.lines[index];
            if (!base) throw new Error("line missing");
            return {
              ...base,
              photos: await photoInputs(
                draft.damaged > 0 || draft.missing > 0 ? draft.photos : [],
              ),
            };
          }),
        ),
      });
    },
    onSuccess: (result) => {
      sending.current = false;
      if (result.ok) {
        setRejection(null);
        queryClient.setQueryData<ReturnParcel | null>(
          ["returns", "parcel", parcel.shipmentId],
          (current) =>
            current ? { ...current, receipt: result.data } : current,
        );
        void queryClient.invalidateQueries({ queryKey: ["returns"] });
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
        void queryClient.invalidateQueries({ queryKey: ["inventory"] });
        onReceived(result.data);
      } else {
        setRejection(result.rejection);
        // "Already received" and the like: show what exists. When someone else's
        // confirm won, say so (by name, from their receipt) and follow it.
        if (result.rejection.kind === "blocked") {
          if (isAlreadyReceived(result.rejection)) onAlreadyReceived();
          void queryClient.invalidateQueries({
            queryKey: ["returns", "parcel", parcel.shipmentId],
          });
        }
      }
    },
    onError: () => {
      sending.current = false;
    },
  });

  const busy = confirm.isPending;
  const ready = problem === null && !noWarehouse;

  // The receipt is recorded before the first write, so while the confirm is out
  // it can be read back, line by line, under its own key (never the parcel's).
  const progress = useQuery({
    queryKey: ["returns", "parcel", parcel.shipmentId, "progress"],
    queryFn: () => data.returns.getParcel(parcel.shipmentId),
    enabled: busy,
    refetchInterval: 700,
    gcTime: 0,
  });
  const writing = progressWords(progress.data?.receipt);

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        // One press is one request: a second tap before the first answers does nothing.
        if (!ready || sending.current) return;
        sending.current = true;
        setRejection(null);
        confirm.mutate();
      }}
    >
      <Facts parcel={parcel} />

      {parcel.warnings.map((warning) => (
        <Warning key={warning.kind}>{warning.message}</Warning>
      ))}
      {parcel.payment === "prepaid" ? (
        <p
          role="note"
          className="flex items-start gap-2 rounded-md border border-transparent bg-tint-blue p-3 text-sm"
        >
          <span>
            <strong>Prepaid: refund needed.</strong> {money(parcel.orderTotal)}{" "}
            was paid online. Confirming creates a follow-up task &ldquo;Refund
            order {parcel.orderNumber}&rdquo;. The refund is done in Shopify;
            the ERP never moves money.
          </span>
        </p>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor={warehouseId} className="text-sm font-medium">
          Put good items back at
        </Label>
        <Select
          value={warehouse === "" ? undefined : warehouse}
          disabled={busy}
          onValueChange={setWarehouse}
        >
          <SelectTrigger
            id={warehouseId}
            // `data-[size=default]:h-9` in the primitive outranks a plain
            // height class, so the 44px has to be written at its strength.
            className="w-full max-sm:data-[size=default]:h-11 pointer-coarse:data-[size=default]:h-11"
          >
            <SelectValue placeholder="Choose a warehouse" />
          </SelectTrigger>
          <SelectContent>
            {parcel.warehouses.map((choice) => (
              <SelectItem key={choice.id} value={choice.id}>
                {choice.name}
                {choice.id === parcel.shippedFrom?.id ? " (shipped from)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {noWarehouse ? (
          <p className="text-destructive text-xs">
            No warehouse here can take stock back: none has an active Shopify
            location.
          </p>
        ) : null}
        {fromServer?.warehouseId ? (
          <p role="alert" className="text-destructive text-xs">
            {fromServer.warehouseId}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">
          Check each item ({parcel.lines.length})
        </h4>
        {fromServer?.lines ? (
          <p role="alert" className="text-destructive w-full text-xs">
            {fromServer.lines}
          </p>
        ) : null}
        <Button
          type="button"
          variant="outline"
          className="max-sm:h-11 pointer-coarse:h-11"
          disabled={busy}
          onClick={() => {
            setDrafts(
              Object.fromEntries(
                parcel.lines.map((line) => [
                  line.orderLineItemId,
                  {
                    ...EMPTY_DRAFT,
                    good: line.quantity,
                  },
                ]),
              ),
            );
          }}
        >
          Mark everything good
        </Button>
      </div>

      <ul className="space-y-3">
        {parcel.lines.map((line) => (
          <LineChecker
            key={line.orderLineItemId}
            line={line}
            draft={draftOf(line.orderLineItemId)}
            warehouseName={warehouseName}
            stockAt={
              line.stock.find((entry) => entry.warehouseId === warehouse)
                ?.available ?? null
            }
            disabled={busy}
            errors={lineErrors.get(line.orderLineItemId) ?? {}}
            onViewImage={() => {
              const at = gallery.findIndex(
                (g) => g.id === line.orderLineItemId,
              );
              setViewing(at < 0 ? null : at);
            }}
            onChange={(draft) => {
              setDrafts({ ...drafts, [line.orderLineItemId]: draft });
            }}
          />
        ))}
      </ul>
      <ImageLightbox
        images={gallery}
        index={viewing}
        onIndexChange={setViewing}
        onClose={() => {
          setViewing(null);
        }}
      />

      <div className="space-y-1.5">
        <Label htmlFor={noteId} className="text-sm font-medium">
          Note{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Textarea
          id={noteId}
          rows={2}
          value={note}
          maxLength={MAX_RETURN_NOTE_LENGTH}
          disabled={busy}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
        {fromServer?.note ? (
          <p role="alert" className="text-destructive text-xs">
            {fromServer.note}
          </p>
        ) : null}
      </div>

      {/*
        Every message the server gave is shown where it belongs: a line's under
        the line, the warehouse's under the warehouse, the note's under the note.
        What is left, and every other kind of refusal, is the notice.
      */}
      {rejection && (rejection.kind !== "invalid" || unplaced.length > 0) ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            setRejection(null);
            void queryClient.invalidateQueries({ queryKey: ["returns"] });
          }}
        />
      ) : rejection ? (
        <p role="alert" className="text-destructive text-sm">
          {rejection.message}
        </p>
      ) : null}

      {/*
        Pinned to the bottom of the panel: on a phone the line list is a long
        scroll, and the count and the button are what the thumb needs at the end of it.
      */}
      <div className="bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky bottom-0 -mx-3 border-t px-3 py-3 backdrop-blur sm:-mx-4 sm:px-4">
        <p className="mb-2 text-sm" aria-live="polite">
          <span className="font-semibold">Good {totals.good}</span>
          {" · "}
          <span
            className={
              totals.damaged > 0 ? "text-destructive font-semibold" : ""
            }
          >
            Damaged {totals.damaged}
          </span>
          {" · "}
          <span>Missing {totals.missing}</span>
          {totals.left > 0 ? (
            <span className="text-amber-700 dark:text-amber-300">
              {" "}
              · {totals.left} still to check
            </span>
          ) : null}
        </p>
        {busy ? (
          <div className="mb-2 space-y-1.5" role="status" aria-live="polite">
            <p className="text-sm font-medium">{writing.words}</p>
            <div className="bg-muted h-1.5 overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-[width] duration-500"
                style={{
                  width: `${String(Math.round(writing.fraction * 100))}%`,
                }}
              />
            </div>
          </div>
        ) : null}
        <Button
          type="submit"
          size="lg"
          className="h-12 w-full text-base max-sm:h-14 pointer-coarse:h-14"
          disabled={!ready || busy}
        >
          {busy ? <Loader2 className="animate-spin" /> : <PackageCheck />}
          {busy
            ? "Confirming…"
            : totals.good > 0
              ? `Confirm and put ${String(totals.good)} back in stock`
              : "Confirm return"}
        </Button>
        {!ready && !busy ? (
          <p className="text-muted-foreground mt-1.5 text-center text-xs">
            {noWarehouse
              ? "Choose a warehouse to continue."
              : totals.left > 0
                ? "Check every item to continue."
                : (problem?.message ?? "Check every item to continue.")}
          </p>
        ) : null}
      </div>
    </form>
  );
}

export function ReceiveParcel({ shipmentId }: { shipmentId: string }) {
  const data = useDataSource();
  const [received, setReceived] = useState<ReturnReceipt | null>(null);
  // This person's own confirm was refused because someone else's won.
  const [lostRace, setLostRace] = useState(false);

  const query = useQuery({
    queryKey: ["returns", "parcel", shipmentId],
    queryFn: () => data.returns.getParcel(shipmentId),
    // While another request is still writing the receipt, keep reading it until
    // it is complete, refused or stale: what is on the screen is then the truth
    // and not the moment the first read happened to catch (#558).
    refetchInterval: (current) =>
      current.state.data?.receipt?.processing ? 1500 : false,
  });

  if (query.isPending) return <TableSkeleton rows={4} />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  const parcel = query.data;
  if (parcel === null) {
    return (
      <EmptyState
        title="Parcel not found"
        description="It may have been removed since the list was loaded."
      />
    );
  }

  const receipt = received ?? parcel.receipt;
  if (receipt) {
    return (
      <div className="space-y-4">
        <Facts parcel={parcel} />
        <ReceiptView
          receipt={receipt}
          showNoUndo={received !== null}
          justReceivedBy={
            lostRace && received === null
              ? (receipt.receivedByName ?? "Someone else")
              : undefined
          }
          onChanged={setReceived}
        />
      </div>
    );
  }

  if (!parcel.receivable) {
    return (
      <div className="space-y-4">
        <Facts parcel={parcel} />
        <EmptyState
          title="Nothing to receive"
          description="This parcel has not failed delivery and is not coming back."
        />
      </div>
    );
  }

  return (
    <ParcelForm
      parcel={parcel}
      onReceived={setReceived}
      onAlreadyReceived={() => {
        setLostRace(true);
      }}
    />
  );
}
